import { useEffect, useState } from 'react';
import { getRoadmapThemes } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';
import { useVideoPopup } from '../../detail/VideoPopup';

const MATURITY_LABELS = ['Initial', 'Emerging', 'Standardised', 'Measured', 'Innovating'];
const EYE = '03 · Roadmap Tool';
let _nextId = 100;

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)',
  letterSpacing: '0.04em', textTransform: 'uppercase',
};
const INPUT = {
  fontFamily: 'inherit', fontSize: 13, background: 'var(--paper-2)',
  border: '1px solid var(--hairline)', padding: '4px 8px',
  color: 'var(--ink)', outline: 'none', width: '100%', boxSizing: 'border-box',
};

function blankTheme() {
  return { id: `thm_new_${++_nextId}`, name: '', maturity_level: 2, levels: ['', '', '', '', ''] };
}

function normalise(t) {
  if (Array.isArray(t.levels)) return t;
  const levels = ['', '', '', '', ''];
  if (t.baseline) levels[2] = t.baseline;
  return { ...t, levels };
}

export function RoadmapDetail({ onClose }) {
  const [themes, setThemes] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editNameId, setEditNameId] = useState(null);
  const [error, setError] = useState(null);
  const vp = useVideoPopup();

  useEffect(() => {
    let cancelled = false;
    getRoadmapThemes()
      .then((d) => { if (!cancelled) setThemes(d.map(normalise)); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, []);

  const updateName = (id, name) =>
    setThemes((ts) => ts.map((t) => (t.id === id ? { ...t, name } : t)));

  const updateLevel = (id, idx, text) =>
    setThemes((ts) =>
      ts.map((t) =>
        t.id === id ? { ...t, levels: t.levels.map((l, i) => (i === idx ? text : l)) } : t
      )
    );

  const addTheme = () => {
    const t = blankTheme();
    setThemes((ts) => [...ts, t]);
    setEditNameId(t.id);
    setSelectedId(t.id);
  };

  const deleteTheme = (id) => {
    setThemes((ts) => ts.filter((t) => t.id !== id));
    if (selectedId === id) setSelectedId(null);
    if (editNameId === id) setEditNameId(null);
  };

  const selectedTheme = themes?.find((t) => t.id === selectedId);

  return (
    <DetailShell eyebrow="03 · Proposition Builder" title="Roadmap Tool" onClose={onClose}>
      {!themes && !error && <div className="tiny" style={{ color: 'var(--ink-3)' }}>Loading…</div>}
      {error && <div className="tiny" style={{ color: 'var(--danger)' }}>Could not load roadmap</div>}
      {themes && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* LEFT: editable process themes list */}
          <div className="card" style={{ alignContent: 'start' }}>
            <SectionTitle
              label="Process themes"
              meta={`${themes.length} THEMES`}
              onPlay={vp.open}
              video={{
                eyebrow: EYE, chapter: '01', duration: '02:48',
                title: 'Process themes — the unit of fractional work',
                description: 'Why themes (not projects) are how you scope, price, and renew. The 6-theme baseline and how to rotate through them.',
                chips: ['Themes', 'Scoping', 'Renewal'],
              }}
            />
            <div style={{ display: 'grid', gap: 6 }}>
              {themes.map((t, i) => (
                <ThemeRow
                  key={t.id}
                  theme={t}
                  index={i}
                  isSelected={selectedId === t.id}
                  isEditingName={editNameId === t.id}
                  onSelect={() => { setSelectedId((id) => (id === t.id ? null : t.id)); setEditNameId(null); }}
                  onEditName={(e) => { e.stopPropagation(); setEditNameId(t.id); }}
                  onNameChange={(name) => updateName(t.id, name)}
                  onNameBlur={() => setEditNameId(null)}
                  onDelete={(e) => { e.stopPropagation(); deleteTheme(t.id); }}
                />
              ))}
              <button
                style={{
                  ...MONO_LABEL, background: 'none',
                  border: '1px dashed var(--hairline)',
                  padding: '8px 10px', cursor: 'pointer',
                  textAlign: 'left', width: '100%',
                }}
                onClick={addTheme}
              >
                + ADD THEME
              </button>
            </div>
          </div>

          {/* RIGHT: maturity matrix or per-theme level editor */}
          <div className="card" style={{ alignContent: 'start' }}>
            {selectedTheme ? (
              <LevelEditor
                theme={selectedTheme}
                onUpdateLevel={(idx, text) => updateLevel(selectedTheme.id, idx, text)}
                onBack={() => setSelectedId(null)}
              />
            ) : (
              <>
                <SectionTitle
                  label="Maturity model"
                  meta="5-STEP"
                  onPlay={vp.open}
                  video={{
                    eyebrow: EYE, chapter: '02', duration: '03:22',
                    title: 'Maturity model — Absent to Referenceable',
                    description: "The 5-step ladder you score every client against. Why 'Mature' is the target and 'Referenceable' is the bonus, not the goal.",
                    chips: ['Scoring', 'Baseline', 'Mature', 'Referenceable'],
                  }}
                />
                <MaturityMatrix themes={themes} onSelectTheme={(id) => setSelectedId((cur) => (cur === id ? null : id))} />
              </>
            )}
          </div>
        </div>
      )}
      {vp.render()}
    </DetailShell>
  );
}

function ThemeRow({ theme, index, isSelected, isEditingName, onSelect, onEditName, onNameChange, onNameBlur, onDelete }) {
  return (
    <div
      style={{
        border: `1px solid ${isSelected ? 'var(--ink)' : 'var(--hairline)'}`,
        background: isSelected ? 'var(--paper-2)' : 'var(--paper)',
        padding: '8px 10px',
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 8,
        alignItems: 'center',
      }}
      onClick={onSelect}
    >
      <span style={MONO_LABEL}>{String(index + 1).padStart(2, '0')}</span>
      {isEditingName ? (
        <input
          autoFocus
          style={INPUT}
          value={theme.name}
          placeholder="Theme name"
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={onNameBlur}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') e.target.blur(); }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span style={{ fontFamily: 'var(--serif)', fontSize: 16 }}>
          {theme.name || <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--sans)', fontSize: 13 }}>Untitled theme</span>}
        </span>
      )}
      <div style={{ display: 'flex', gap: 2 }}>
        <button
          style={{ ...MONO_LABEL, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px' }}
          title="Rename"
          onClick={onEditName}
        >
          ✎
        </button>
        <button
          style={{ ...MONO_LABEL, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px' }}
          title="Delete"
          onClick={onDelete}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function MaturityMatrix({ themes, onSelectTheme }) {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '120px repeat(5, 1fr)',
          border: '1px solid var(--ink)',
          marginTop: 4,
        }}
      >
        <div style={{ background: 'var(--ink)', color: 'var(--paper)', padding: 6 }} className="tiny">THEME</div>
        {MATURITY_LABELS.map((l) => (
          <div
            key={l}
            style={{ background: 'var(--ink)', color: 'var(--paper)', padding: 6, borderLeft: '1px solid var(--paper-3)' }}
            className="tiny"
          >
            {l}
          </div>
        ))}
        {themes.map((t) => (
          <MaturityRow key={t.id} theme={t} onClick={() => onSelectTheme(t.id)} />
        ))}
      </div>
      <div className="tiny" style={{ marginTop: 8, color: 'var(--ink-3)' }}>
        Click a theme name to define what each level means →
      </div>
    </>
  );
}

function MaturityRow({ theme, onClick }) {
  return (
    <>
      <div
        style={{ padding: 8, borderTop: '1px solid var(--hairline)', fontSize: 12, cursor: 'pointer' }}
        onClick={onClick}
        title="Click to define levels"
      >
        {theme.name || <span style={{ color: 'var(--ink-4)' }}>Untitled</span>}
      </div>
      {[0, 1, 2, 3, 4].map((k) => {
        const hasDesc = theme.levels?.[k]?.trim();
        return (
          <div
            key={k}
            style={{
              padding: 8,
              borderTop: '1px solid var(--hairline)', borderLeft: '1px solid var(--hairline)',
              background: 'var(--paper)',
              textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)',
            }}
            title={hasDesc || undefined}
          >
            {hasDesc ? '●' : '○'}
          </div>
        );
      })}
    </>
  );
}

function LevelEditor({ theme, onUpdateLevel, onBack }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={MONO_LABEL}>DEFINE LEVELS</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 20, marginTop: 4 }}>
            {theme.name || <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--sans)', fontSize: 14 }}>Untitled theme</span>}
          </div>
        </div>
        <button
          style={{ ...MONO_LABEL, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', whiteSpace: 'nowrap', marginTop: 2 }}
          onClick={onBack}
        >
          MATRIX →
        </button>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {MATURITY_LABELS.map((label, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, alignItems: 'start' }}>
              <div style={{ ...MONO_LABEL, paddingTop: 7 }}>
                {label}
              </div>
              <textarea
                rows={2}
                style={{
                  ...INPUT,
                  resize: 'vertical',
                  minHeight: 44,
                  lineHeight: 1.4,
                }}
                placeholder={`What does ${label.toLowerCase()} look like for this theme?`}
                value={theme.levels?.[i] ?? ''}
                onChange={(e) => onUpdateLevel(i, e.target.value)}
              />
            </div>
          ))}
      </div>
    </div>
  );
}

export default RoadmapDetail;
